import React, {createContext, useCallback, useContext, useEffect, useMemo, useReducer} from "react";

import * as api from "../../services/api";
import {useFeedbackTools} from "../../components/FeedbackTools";
import useDialog from "../../hooks/useDialog";
import {homeMapper} from "../../mapper/homeMapper";
import {locationMapper} from "../../mapper/locationsMapper";
import {homeServiceMapper} from "../../mapper/homeServiceMapper";
import {apartmentServiceMapper} from "../../mapper/apartmentServiceMapper";
import {apartmentTypologyMapper} from "../../mapper/apartmentTypologyMapper";
import {apartmentCategoryMapper} from "../../mapper/apartmentCategoryMapper";


const AccommodationsContext = createContext();
export const useAccommodationsPage = () => useContext(AccommodationsContext)

export default function AccommodationsProvider({children}) {

    const [state, dispatcher] = useReducer(
        (state, action) => {
            switch (action.type) {
                case 'setAttributes':
                    return {...state, ...action.attributes}
                case 'createHome':
                    return {...state, homes: [...state.homes, action.home]}
                case 'deleteHome':
                    const newHomes = state.homes.filter(h => h.id !== action.homeId);
                    const newSelectedHome = newHomes.length > 0 ? newHomes[0] : null;
                    return {...state, homes: newHomes, selectedHome: newSelectedHome}
                default:
                    throw new Error(`Cannot find action ${action.type} for homeState reducer`);
            }
        },
        {
            homes: [],
            locations: [],
            homeServices: [],
            apartmentServices: [],
            apartmentCategories: [],
            apartmentTypologies: [],
            selectedHome: null,
            isLoading: true,
        }
    );

   const formCreateHome = useDialog();

    // Feedback tools
    const {showErrorNotification} = useFeedbackTools();

    // Load all the homes as soon as the context is created
    useEffect(() => {
        const loadData = async () => {
            try {
                const [homes, locations, homeServices, apartmentServices, apartmentTypologies, apartmentCategories] = await Promise.all([
                    api.Homes.getAllHomes(),
                    api.Locations.getAllLocations(),
                    api.HomeServices.getAllHomeServices(),
                    api.Apartments.getAllApartmentServices(),
                    api.Apartments.getAllApartmentTypologies(),
                    api.Apartments.getAllApartmentCategories(),
                ]);

                dispatcher({
                    type: "setAttributes", attributes: {
                        homes: homes.map(h => homeMapper(h)),
                        locations: locations.map(l => locationMapper(l)),
                        homeServices: homeServices.map(s => homeServiceMapper(s)),
                        apartmentServices: apartmentServices.map(s => apartmentServiceMapper(s)),
                        apartmentTypologies: apartmentTypologies.map(t => apartmentTypologyMapper(t)),
                        apartmentCategories: apartmentCategories.map(c => apartmentCategoryMapper(c)),
                        selectedHome: homes.length > 0 ? homes[0] : null,
                        isLoading: false
                    }
                })
            } catch (e) {
                showErrorNotification(e);
                dispatcher({type: "setAttributes", attributes: {isLoading: false}})
            }
        };

        loadData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const createHome = useCallback(
        async (data) => {
            const homeData = await api.Homes.createHome(data);
            const home = homeMapper(homeData);
            dispatcher({type: "createHome", home: home});
            return home;
        }, [dispatcher]
    );

    const deleteHome = useCallback(
        (homeId) => {
            dispatcher({type: "deleteHome", homeId: homeId})
        },
        [dispatcher]
    );

    const onHomeSelection = (home) => {
        dispatcher({type: "setAttributes", attributes: {selectedHome: home}});
    }

    const apartmentTypologyMap = useMemo(
        () => state.apartmentTypologies.reduce((acc, item) => {
            acc[item.id] = item;
            return acc;
        }, {}),
        [state.apartmentTypologies]
    );

    const apartmentCategoryMap = useMemo(
        () => state.apartmentCategories.reduce((acc, item) => {
            acc[item.id] = item;
            return acc;
        }, {}),
        [state.apartmentCategories]
    );

    const locationMap = useMemo(
        () => state.locations.reduce((acc, item) => {
            acc[item.id] = item;
            return acc;
        }, {}),
        [state.locations]
    );

    const context = {
        homes: state.homes,
        locations: state.locations,
        homeServices: state.homeServices,
        apartmentServices: state.apartmentServices,
        apartmentTypologies: state.apartmentTypologies,
        apartmentCategories: state.apartmentCategories,
        selectedHomeId: state.selectedHome?.id,
        selectedHomeName: state.selectedHome?.name || "",
        isLoading: state.isLoading,
        apartmentTypologyMap,
        apartmentCategoryMap,
        locationMap,
        formCreateHome,
        createHome,
        deleteHome,
        onHomeSelection,
    }

    return (
        <AccommodationsContext.Provider value={context}>
            {children}
        </AccommodationsContext.Provider>
    )
}